import {
  calculateDaysPastDue,
  calculateServicingLTV,
  convertDateInputTo8AMLocal,
  dateToEpoch,
  formatDateForInputWithOffset,
  formatIsoDate,
  getTimestampAsDate,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
  buildFullName,
} from '../../common/UtilityFunctions';
import Styles from '../../css/LoanInformation.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { IconButton, Tooltip } from '@mui/material';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import { NumericFormat } from 'react-number-format';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLDCContext } from '../../contexts/LoanDefaultCodesContext';
import { NavLink } from 'react-router-dom';
import DDSCodeList from '../loan-default-codes/List';
import useQidList from '../../common/hooks/useQidList';
import * as zipcodeToTimezone from 'zipcode-to-timezone';
import SelectDropdown from '../loan-default-codes/Dropdown';
import { InfoOutlined } from '@mui/icons-material';
import useUserSearch from '../../common/hooks/useUserSearch';
import { useAuthContext } from '../../contexts/AuthContext';

const LoanServicing = () => {
  const {
    servicingData,
    propData,
    lnptData,
    handleLoanChange,
    loanData,
    lastPaymentData,
    paymentHistoryData,
    lossMitData,
    handleLossMitiChange,
    loanEvents,
    handleLoanEventChange,
    removedStopIndices,
    setRemovedStopIndices,
    addedStopCodes,
    setAddedStopCodes,
    borrowersArr,
    originalLoanData,
  } = useLoanInformationContext();
  const { fullAccessSuperUser } = useAuthContext();
  const { LDCInfo, LDCFunctions, filteredDDRCodeOptions, filteredServicingOccupancyOptions, loanPropCode } =
    useLDCContext();

  const originalServicingStopCodes = originalLoanData?.servicingStopCodes;

  const { handleLDCChange } = LDCFunctions;
  const { bkChapter, ddsCodesListForLoan, servicingOccupancy } = LDCInfo;
  const { ServOccuStatusVerifiedDate, bkFileDate } = LDCInfo.dates;

  const { qid: modTypeQid } = useQidList('LOSSMITI.005');

  //This makes sure to check for the needed Servicing LTV fields after prop and lnpt data is done loading
  const servicingLTV =
    !isObjEmptyOrNull(propData) && !isObjEmptyOrNull(lnptData)
      ? calculateServicingLTV(lnptData?.upb, propData?.marketValue, propData?.purchasePrice)
      : null;

  //This calculates days past due once loan data has loaded using lpi
  const daysPastDue = !isObjEmptyOrNull(loanData)
    ? calculateDaysPastDue(loanData?.lpi, loanData?.servicingState)
    : null;

  const payHistoryChars = useMemo(() => {
    const raw = loanData?.payHistory || '';
    const arr = raw.split('');
    while (arr.length < 12) arr.push(' ');
    return arr.slice(0, 12);
  }, [loanData?.payHistory]);

  const handlePayCharChange = useCallback(
    (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value.slice(-1).toUpperCase();
      const charToSet = input === '' ? ' ' : input;
      const newArr = [...payHistoryChars];
      newArr[idx] = charToSet;
      const newString = newArr.join('');
      handleLoanChange('edit', 'payHistory', newString);
    },
    [payHistoryChars, handleLoanChange],
  );

  const delinqChars = useMemo(() => {
    const raw = paymentHistoryData?.paymentHistory || '';
    const arr = raw.split('');
    while (arr.length < 24) arr.push(' ');
    return arr.slice(0, 24);
  }, [paymentHistoryData?.paymentHistory]);

  const { users: loanOfficerUsers } = useUserSearch({
    ID: loanData?.interviewerUid,
    returnFields: ['nameFirst', 'nameMiddle', 'nameLast'],
  });

  const historyDates = useMemo(() => {
    const base = new Date();
    base.setDate(1);

    return Array.from({ length: 24 }, (_, i) => {
      const dt = new Date(base);
      dt.setMonth(base.getMonth() - 1 - i);
      return dt;
    });
  }, []);

  const monthLabels = useMemo(() => {
    const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'short' });
    return historyDates.map((dt) => monthFmt.format(dt));
  }, [historyDates]);

  const yearGroups = useMemo(() => {
    type YearGroup = { year: number; count: number };
    const groups: YearGroup[] = [];
    historyDates.forEach((dt) => {
      const y = dt.getFullYear();
      const found = groups.find((g) => g.year === y);
      if (found) found.count++;
      else groups.push({ year: y, count: 1 });
    });
    return groups;
  }, [historyDates]);
  const [showDelinqKey, setShowDelinqKey] = useState(false);
  const getCode = (o: any) => o?.code ?? o?.value ?? o?.id ?? '';
  const ddrReasonIsInList = useMemo(() => {
    if (loanData?.ddrReason == null || String(loanData?.ddrReason).trim() === '') return true;
    const code = String(loanData?.ddrReason).trim();
    return (filteredDDRCodeOptions ?? []).some((o) => String(getCode(o)).trim() === code);
  }, [loanData?.ddrReason, filteredDDRCodeOptions]);

  const getMinStopCodeIndex = useCallback(
    (newStopCodeObjs: ServicingStopCode[]): number => {
      const servStopCodeArr = newStopCodeObjs.length > 0 ? newStopCodeObjs : originalServicingStopCodes;

      return servStopCodeArr && servStopCodeArr.length > 0 ? Math.max(...servStopCodeArr.map((i) => i.$index)) + 1 : 0;
    },
    [originalServicingStopCodes],
  );

  const getAvailableIndices = useCallback(
    (removed: Set<number>): Set<number> => {
      const indicesInServStopCodes = originalServicingStopCodes ? originalServicingStopCodes.map((i) => i.$index) : [];
      const maxIndex = indicesInServStopCodes.length > 0 ? Math.max(...indicesInServStopCodes) : -1;
      const availableIndices: Set<number> = new Set(removed);
      for (let i = 0; i <= maxIndex; i++) {
        if (!indicesInServStopCodes.includes(i)) {
          availableIndices.add(i);
        }
      }
      return availableIndices;
    },
    [originalServicingStopCodes],
  );

  const getFinalServicingStopCodesObject = useCallback(
    (added: Set<string>, removed: Set<number>): ServicingStopCode[] => {
      var finalRemovedStopIndices: Set<number> = new Set(removed);
      var newStopCodeObjs: ServicingStopCode[] = [];

      var availableIndices = getAvailableIndices(removed);
      added.forEach((i) => {
        var minIndex: number;
        if (availableIndices.size > 0) {
          minIndex = Math.min(...availableIndices);
          availableIndices.delete(minIndex);
          finalRemovedStopIndices.delete(minIndex);
        } else {
          minIndex = getMinStopCodeIndex(newStopCodeObjs);
        }
        newStopCodeObjs.push({
          $index: minIndex,
          servStopCode: i,
        });
      });
      finalRemovedStopIndices.forEach((i) =>
        newStopCodeObjs.push({
          $index: i,
          servStopCode: '',
        }),
      );

      return newStopCodeObjs;
    },
    [getMinStopCodeIndex, getAvailableIndices],
  );

  const removeStopCode = useCallback(
    (removeIndex: number) => {
      var next: Set<number> = new Set();
      setRemovedStopIndices((prev) => {
        next = new Set(prev);
        next.add(removeIndex);
        return next;
      });
      handleLoanChange('edit', 'servicingStopCodes', getFinalServicingStopCodesObject(addedStopCodes, next));
    },
    [handleLoanChange, getFinalServicingStopCodesObject, addedStopCodes],
  );

  const { qid: stopCodesQID, loadingQid: stopCodesQidLoading } = useQidList('MASK_CD.S301');

  const { qid: GNMAForbearanceQID } = useQidList('LOSSMITI.007');

  const getLabel = (o: any) => o?.label ?? o?.description ?? o?.text ?? o?.name ?? '';

  //for pay history modal
  const [showPayHistoryKey, setShowPayHistoryKey] = useState(false);
  const [localTime, setLocalTime] = useState(null);
  const firstZip = borrowersArr.find(
    (b) => b.borrowerNumber == loanData?.primaryBorrowerNumber && b.presentZipCodeFirstFive,
  )?.presentZipCodeFirstFive;

  const zone = firstZip ? zipcodeToTimezone.lookup(firstZip) : undefined;

  const reloadLocalTime = useCallback(() => {
    setLocalTime(
      zone
        ? new Date().toLocaleTimeString('en-US', {
            timeZone: zone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })
        : '',
    );
  }, [zone]);

  useEffect(() => {
    reloadLocalTime();
    const reloadDuration = setInterval(reloadLocalTime, 30000);
    return () => clearInterval(reloadDuration);
  }, [zone]);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const PAY_HISTORY_KEY: Record<string, string> = {
    '0': 'Prepaid',
    '1': '1st through 5th',
    '2': '6th through 16th',
    '3': '17th through 25th',
    '4': '26th through EOM',
    '5': 'Over 30 days late',
    '6': 'Over 60 days late',
    '7': 'Over 90 days late',
    '8': 'Over 120 days late',
    X: 'Report to credit bureau as no data available',
  };
  const DELINQ_KEY: Record<string, string> = {
    '0': '00-29 days past due',
    '1': '30-59 days past due',
    '2': '60-89 days past due',
    '3': '90-119 days past due',
    '4': '120-149 days past due',
    '5': '150-179 days past due',
    '6': '180 or more days past due',
    B: 'No payment history available prior to this time - either because the account was not open or because the payment history cannot be furnished. ',
    D: 'No payment history reported/available for this month',
    E: 'Zero balance and current account',
    G: 'Collections',
    H: 'Foreclosure completed',
    J: 'Voluntary surrender',
    K: 'Repossession',
    L: 'Charge-off',
  };
  type EditableEventDateCode = 'TPP' | 'TE1' | 'DTM' | 'FDS' | 'DAX' | 'DAK';

  const handleEventDateChange = useCallback(
    (code: EditableEventDateCode, value: string) => {
      if (!value) {
        handleLoanEventChange('edit', `${code}.activeDate`, '');
        handleLoanEventChange('edit', `${code}.active`, false);
        return;
      }

      const localDate = convertDateInputTo8AMLocal(value);
      const timestamp = dateToEpoch(localDate);

      handleLoanEventChange('edit', `${code}.activeDate`, timestamp);
      handleLoanEventChange('edit', `${code}.active`, true);
    },
    [handleLoanEventChange],
  );

  return (
    <EnvelopeWithBox blockName='Loan Servicing Information'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Loan Paid-To-Date:</th>
                <td>{loanData?.lpi ? getTimestampAsDate(loanData?.lpi) : ''}</td>
              </tr>
              <tr>
                <th>Last Payment Date:</th>
                <td>{lastPaymentData?.datePmntRecd ? getTimestampAsDate(lastPaymentData?.datePmntRecd) : ''}</td>
              </tr>
              <tr>
                <th>Grace Period # Days:</th>
                <td>
                  <NumericFormat
                    value={loanData?.gracePer ? loanData?.gracePer : 0}
                    decimalScale={0}
                    disabled={!fullAccessSuperUser}
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'gracePer', values.floatValue);
                      if (originalLoanData?.gracePer == values.floatValue) {
                        handleLoanChange('cancel', 'depProtectGracePer');
                      } else {
                        handleLoanChange('edit', 'depProtectGracePer', false);
                      }
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Late Charge Day:</th>
                <td>{loanData?.gracePer ? loanData?.gracePer + 1 : ''}</td>
              </tr>
              <tr>
                <th>Auto Pay:</th>
                <td>{servicingData?.autoPayStatusKeyword ? servicingData.autoPayStatusKeyword : ''}</td>
              </tr>
              <tr>
                <th>Bankruptcy:</th>
                <td>{servicingData?.defaultBankruptcyStatus ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Bankruptcy Chapter:</th>
                <td>{bkChapter ? bkChapter : ''}</td>
              </tr>
              <tr>
                <th>Bankruptcy File Date:</th>
                <td>{bkFileDate ? formatIsoDate(bkFileDate) : ''}</td>
              </tr>
              <tr>
                <th>Paid Off:</th>
                <td>{lnptData?.upb === 0 ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Foreclosure:</th>
                <td>{loanData?.foreclosureMainStatus ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Repo Status:</th>
                <td> </td>
              </tr>
              <tr>
                <th>Credit Bureau Consumer Info: </th>
                <td>{servicingData?.CRAConsumerInfo ? servicingData?.CRAConsumerInfo : ''}</td>
              </tr>
              <tr>
                <th>Credit Bureau Status: </th>
                <td>{servicingData?.CRAStatus ? servicingData?.CRAStatus : ''}</td>
              </tr>
              <tr>
                <th>CB Special Comment: </th>
                <td>{servicingData?.CRASpecialInfo ? servicingData?.CRASpecialInfo : ''}</td>
              </tr>
              <tr>
                <th>Stop Codes:</th>
                <td>
                  {stopCodesQidLoading ? (
                    <span>Loading stop codes…</span>
                  ) : (
                    <>
                      {(originalServicingStopCodes || [])
                        .filter((c) => !removedStopIndices.has(c.$index))
                        .map((code) => (
                          <div key={code.$index} className={Styles.addedStopCode}>
                            <div key={code.$index}>
                              {code.servStopCode} - {code.servStopCodeDescription}
                            </div>
                            <button type='button' onClick={() => removeStopCode(code.$index)}>
                              &times;
                            </button>
                          </div>
                        ))}
                      {[...addedStopCodes].map((c, i) => (
                        <div className={Styles.addedStopCode}>
                          <div key={i}>
                            {c} - {stopCodesQID.find((i) => i.code == c).description}
                          </div>
                          <button
                            type='button'
                            onClick={(e) => {
                              var next: Set<string> = new Set();
                              setAddedStopCodes((prev) => {
                                next = new Set(prev);
                                next.delete(c);
                                return next;
                              });

                              handleLoanChange(
                                'edit',
                                'servicingStopCodes',
                                getFinalServicingStopCodesObject(next, removedStopIndices),
                              );
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                      <select
                        value={''}
                        onChange={(e) => {
                          var next: Set<string> = new Set();
                          setAddedStopCodes((prev) => {
                            next = new Set(prev);
                            next.add(e.target.value);
                            return next;
                          });

                          handleLoanChange(
                            'edit',
                            'servicingStopCodes',
                            getFinalServicingStopCodesObject(next, removedStopIndices),
                          );
                        }}
                        disabled={stopCodesQidLoading}
                        className={Styles.formInput}
                        style={{ marginTop: '0.5rem' }}
                      >
                        <option value='' disabled>
                          -- Add a Stop Code --
                        </option>
                        {stopCodesQID
                          .filter((i) => !addedStopCodes.has(i.code))
                          .filter(
                            (i) =>
                              !originalServicingStopCodes
                                ?.filter((orig) => !removedStopIndices.has(orig.$index))
                                .map((i) => i.servStopCode)
                                .includes(i.code),
                          )
                          .map((opt, j) => (
                            <option key={j} value={opt.code}>
                              {opt.code} - {opt.description}
                            </option>
                          ))}
                      </select>
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>Loan Officer:</th>
                <td>
                  {[buildFullName(loanOfficerUsers[0]), (loanData?.interviewerUid || '').trim()]
                    .filter(Boolean)
                    .join(' - ')}
                </td>
              </tr>
              <tr>
                <th>Assumption Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='MCD'
                    value={loanEvents?.MCD?.activeDate ? formatDateForInputWithOffset(loanEvents?.MCD?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'MCD.activeDate', '');
                        handleLoanEventChange('edit', 'MCD.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'MCD.activeDate', ts);
                      handleLoanEventChange('edit', 'MCD.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Days Past Due: </th>
                <td>{daysPastDue ? daysPastDue : 0}</td>
              </tr>
              <tr>
                <th>Acquisition Date: </th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='SRC'
                    value={loanEvents?.SRC?.activeDate ? formatDateForInputWithOffset(loanEvents?.SRC?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'SRC.activeDate', '');
                        handleLoanEventChange('edit', 'SRC.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'SRC.activeDate', ts);
                      handleLoanEventChange('edit', 'SRC.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Sale Date: </th>
                <td>{servicingData?.ownerXferDate ? getTimestampAsDate(servicingData?.ownerXferDate) : ''}</td>
              </tr>
              <tr>
                <th>Forbearance Start Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='DTM'
                    value={loanEvents?.DTM?.activeDate ? formatDateForInputWithOffset(loanEvents.DTM.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('DTM', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Forbearance End Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='FDS'
                    value={loanEvents?.FDS?.activeDate ? formatDateForInputWithOffset(loanEvents.FDS.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('FDS', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Partial Claim Start Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='DAX'
                    value={loanEvents?.DAX?.activeDate ? formatDateForInputWithOffset(loanEvents.DAX.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('DAX', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Partial Claim End Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='DAK'
                    value={loanEvents?.DAK?.activeDate ? formatDateForInputWithOffset(loanEvents.DAK.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('DAK', e.target.value)}
                  />
                </td>
              </tr>
              <>
                <tr>
                  <th>
                    <div>
                      <span>Payment History:</span>
                      <Tooltip title='Info'>
                        <IconButton className={Styles.icon} onClick={() => setShowPayHistoryKey(true)} size='small'>
                          <InfoOutlined className={Styles.icon} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </th>
                  <td>
                    <table className={Styles.payHistoryGrid}>
                      <thead>
                        <tr>
                          {MONTHS.map((m) => (
                            <th key={m}>{m}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {payHistoryChars.map((c, i) => (
                            <td key={i}>
                              <input
                                type='text'
                                maxLength={1}
                                value={c === ' ' ? '' : c}
                                onChange={handlePayCharChange(i)}
                                className={Styles.payHistoryInput}
                              />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                {showPayHistoryKey && (
                  <div className={Styles.payHistoryModalOverlay} onClick={() => setShowPayHistoryKey(false)}>
                    <div className={Styles.payHistoryModalContent} onClick={(e) => e.stopPropagation()}>
                      <h2>Payment History Codes</h2>
                      <ul className={Styles.payHistoryLegendList}>
                        {Object.entries(PAY_HISTORY_KEY).map(([code, desc]) => (
                          <li key={code}>
                            <strong>{code}</strong> — {desc}
                          </li>
                        ))}
                      </ul>
                      <button
                        type='button'
                        onClick={() => setShowPayHistoryKey(false)}
                        className={Styles.payHistoryCloseButton}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Servicing Occupancy:</th>
                <td className={Styles.servOccCell}>
                  <SelectDropdown
                    name='servicingOccupancy'
                    label='Servicing Occupancy'
                    value={servicingOccupancy ?? ''}
                    options={filteredServicingOccupancyOptions}
                    func={(e) => {
                      handleLDCChange('edit', 'servicingOccupancy', e.target.value);
                      handleLDCChange('edit', 'propCode', loanPropCode);
                    }}
                    renderMode='control'
                  />
                </td>
              </tr>
              <tr>
                <th>Loan Mod Type: </th>
                <td>
                  <select
                    value={lossMitData?.modificationType ? lossMitData?.modificationType : ''}
                    onChange={(e) => handleLossMitiChange('edit', 'modificationType', e.target.value)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    {modTypeQid.map((opt, i) => (
                      <option key={i} value={opt.code}>
                        {opt.code} - {opt.description}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Statement Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='SER'
                    value={loanEvents?.SER?.activeDate ? formatDateForInputWithOffset(loanEvents?.SER?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'SER.activeDate', '');
                        handleLoanEventChange('edit', 'SER.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'SER.activeDate', ts);
                      handleLoanEventChange('edit', 'SER.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Statement Sent: </th>
                <td>{lnptData?.statementSent ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>HUD DDR Reason:</th>
                <td>
                  <select
                    name='ddrReason'
                    value={loanData?.ddrReason ?? loanData?.ddrReason ?? ''}
                    onChange={(e) => handleLoanChange('edit', 'ddrReason', e.target.value)}
                  >
                    <option value=''>Select…</option>
                    {!ddrReasonIsInList && !!loanData?.ddrReason && (
                      <option value={String(loanData?.ddrReason)}>{String(loanData?.ddrReason)}</option>
                    )}

                    {(filteredDDRCodeOptions ?? []).map((o: any) => {
                      const code = String(getCode(o) ?? '').trim();
                      const label = String(getLabel(o) ?? '').trim();
                      const text = code && label ? `${code} - ${label}` : code ? code : label;
                      const value = code || label;
                      return (
                        <option key={`${code || label}`} value={value}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Occupancy Status Verified:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='ServOccuStatusVerifiedDate'
                    value={ServOccuStatusVerifiedDate.activeDate}
                    onChange={(e) => {
                      handleLDCChange('edit', 'dates.ServOccuStatusVerifiedDate.activeDate', e.target.value);
                      if (isStrEmptyOrNull(e.target.value)) {
                        handleLDCChange('edit', 'dates.ServOccuStatusVerifiedDate.active', false);
                      } else {
                        handleLDCChange('edit', 'dates.ServOccuStatusVerifiedDate.active', true);
                      }
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>HUD DDR Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='ddrDate'
                    value={loanEvents?.SRG?.activeDate ? formatDateForInputWithOffset(loanEvents?.SRG?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'SRG.activeDate', '');
                        handleLoanEventChange('edit', 'SRG.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const newDdrDate = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'SRG.activeDate', newDdrDate);
                      handleLoanEventChange('edit', 'SRG.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Credit Grade: </th>
                <td>{loanData?.uwCreditGrade ? loanData?.uwCreditGrade : ''}</td>
              </tr>
              <tr>
                <th>Disaster Name:</th>
                <td>
                  <input
                    type='text'
                    name='disasterName'
                    value={loanData?.disasterName || ''}
                    onChange={(e) => handleLoanChange('edit', 'disasterName', e.target.value)}
                    placeholder='Disaster Name'
                  />
                </td>
              </tr>
              <tr>
                <th>Disaster Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='S7B'
                    value={loanEvents?.S7B?.activeDate ? formatDateForInputWithOffset(loanEvents?.S7B?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'S7B.activeDate', '');
                        handleLoanEventChange('edit', 'S7B.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'S7B.activeDate', ts);
                      handleLoanEventChange('edit', 'S7B.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Disaster Not Impacted:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='S7A'
                    value={loanEvents?.S7A?.activeDate ? formatDateForInputWithOffset(loanEvents?.S7A?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'S7A.activeDate', '');
                        handleLoanEventChange('edit', 'S7A.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'S7A.activeDate', ts);
                      handleLoanEventChange('edit', 'S7A.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Disaster Property Impacted:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='S79'
                    value={loanEvents?.S79?.activeDate ? formatDateForInputWithOffset(loanEvents?.S79?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'S79.activeDate', '');
                        handleLoanEventChange('edit', 'S79.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'S79.activeDate', ts);
                      handleLoanEventChange('edit', 'S79.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Disaster Pay Impacted:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='S78'
                    value={loanEvents?.S78?.activeDate ? formatDateForInputWithOffset(loanEvents?.S78?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'S78.activeDate', '');
                        handleLoanEventChange('edit', 'S78.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'S78.activeDate', ts);
                      handleLoanEventChange('edit', 'S78.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Moratorium Ext:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='MOE'
                    value={loanEvents?.MOE?.activeDate ? formatDateForInputWithOffset(loanEvents?.MOE?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'MOE.activeDate', '');
                        handleLoanEventChange('edit', 'MOE.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'MOE.activeDate', ts);
                      handleLoanEventChange('edit', 'MOE.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>GNMA Forbearance Exit Reason:</th>
                <td>
                  {GNMAForbearanceQID?.length == 0 ? (
                    <span>Loading</span>
                  ) : (
                    <select
                      name='forbearanceExitReason'
                      value={
                        isStrEmptyOrNull(lossMitData?.forbearanceExitReason)
                          ? 'disabled'
                          : lossMitData?.forbearanceExitReason
                      }
                      onChange={(e) => handleLossMitiChange('edit', 'forbearanceExitReason', e.target.value)}
                    >
                      <option value='disabled' disabled>
                        Select status…
                      </option>
                      <option value=''>None</option>
                      {GNMAForbearanceQID.map((opt, i) => (
                        <option key={i} value={opt.code}>
                          {opt.code} - {opt.description}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
              <tr>
                <th>TPP Start Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='TPP'
                    value={loanEvents?.TPP?.activeDate ? formatDateForInputWithOffset(loanEvents.TPP.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('TPP', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>TPP End Date</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='TE1'
                    value={loanEvents?.TE1?.activeDate ? formatDateForInputWithOffset(loanEvents.TE1.activeDate) : ''}
                    onChange={(e) => handleEventDateChange('TE1', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Servicing LTV: </th>
                <td>
                  {servicingLTV ? (
                    <NumericFormat
                      displayType='text'
                      value={servicingLTV}
                      decimalScale={2}
                      fixedDecimalScale
                      suffix='%'
                      thousandSeparator
                    />
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Selling Price: </th>
                <td>
                  {loanData?.purchasePrice ? (
                    <NumericFormat
                      displayType='text'
                      value={loanData?.purchasePrice}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Local Time: </th>
                <td>{localTime}</td>
              </tr>
              <>
                <tr>
                  <th>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>Delinquency History:</span>
                      <Tooltip title='Info'>
                        <IconButton size='small' onClick={() => setShowDelinqKey(true)}>
                          <InfoOutlined className={Styles.icon} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </th>
                  <td>
                    {!paymentHistoryData ? (
                      'Loading…'
                    ) : (
                      <table className={Styles.payHistoryGrid}>
                        <thead>
                          <tr>
                            {yearGroups.map(({ year, count }) => (
                              <th key={year} colSpan={count}>
                                {year}
                              </th>
                            ))}
                          </tr>
                          <tr>
                            {monthLabels.map((m, i) => (
                              <th key={i}>{m}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {delinqChars.map((c, i) => (
                              <td key={i}>
                                <input
                                  type='text'
                                  value={c === ' ' ? '' : c}
                                  readOnly
                                  className={Styles.payHistoryInput}
                                />
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>

                {showDelinqKey && (
                  <div className={Styles.payHistoryModalOverlay} onClick={() => setShowDelinqKey(false)}>
                    <div className={Styles.payHistoryModalContent} onClick={(e) => e.stopPropagation()}>
                      <h2>Delinquency History Codes</h2>
                      <ul className={Styles.payHistoryLegendList}>
                        {Object.entries(DELINQ_KEY).map(([code, desc]) => (
                          <li key={code}>
                            <strong>{code}</strong> — {desc}
                          </li>
                        ))}
                      </ul>
                      <button
                        type='button'
                        className={Styles.payHistoryCloseButton}
                        onClick={() => setShowDelinqKey(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </>
              <tr>
                <th className={Styles.ddscodesheader}>
                  <NavLink to='/loan-default-codes'>DDS Codes</NavLink>
                </th>
                <td>{ddsCodesListForLoan.length !== 0 && <DDSCodeList list={ddsCodesListForLoan}></DDSCodeList>}</td>
              </tr>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default LoanServicing;
